import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { GPUCatalogSummary, SemanticGPUResponse } from '../../types/gpu';
import { getApiUrl } from '../../utils/api';

interface CatalogHealth {
  status: 'healthy' | 'degraded' | 'critical';
  issues: string[];
  stale_data_count: number;
  total_offerings: number;
  freshness_score: number;
  coverage_score: number;
}

type InstanceStatus = 'launchable' | 'no_quota' | 'no_image' | 'no_pricing' | 'arch_mismatch';

function getInstanceStatus(gpu: SemanticGPUResponse): InstanceStatus {
  if (gpu.image_status === 'architecture_mismatch') return 'arch_mismatch';
  if (gpu.image_status === 'no_image') return 'no_image';
  if (gpu.quota_available === false) return 'no_quota';
  if (gpu.on_demand_price_per_hour == null) return 'no_pricing';
  return 'launchable';
}

const STATUS_CONFIG: Record<InstanceStatus, { label: string; dotClass: string }> = {
  launchable: { label: 'Launchable', dotClass: 'bg-green-500' },
  no_quota: { label: 'No Quota', dotClass: 'bg-red-500' },
  no_image: { label: 'No Image', dotClass: 'bg-yellow-500' },
  no_pricing: { label: 'No Pricing', dotClass: 'bg-orange-500' },
  arch_mismatch: { label: 'Arch Mismatch', dotClass: 'bg-amber-500' },
};

export default function GPUCatalogOverview() {
  const [summary, setSummary] = useState<GPUCatalogSummary | null>(null);
  const [health, setHealth] = useState<CatalogHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Instance-level catalog data (admin view)
  const [instances, setInstances] = useState<SemanticGPUResponse[]>([]);
  const [instancesLoading, setInstancesLoading] = useState(false);
  const [instancesExpanded, setInstancesExpanded] = useState(false);
  const [statusFilter, setStatusFilter] = useState<InstanceStatus | ''>('');

  const fetchCatalogData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch catalog summary
      const [summaryResponse] = await Promise.allSettled([
        axios.get(`${getApiUrl()}/api/gpu/catalog/summary`, { headers })
      ]);

      if (summaryResponse.status === 'fulfilled') {
        setSummary(summaryResponse.value.data);

        // Calculate health metrics
        const data = summaryResponse.value.data;
        const healthMetrics: CatalogHealth = {
          status: data.data_quality_score > 0.8 ? 'healthy' : data.data_quality_score > 0.6 ? 'degraded' : 'critical',
          issues: [],
          stale_data_count: 0, // Would need additional API endpoint
          total_offerings: data.total_offerings,
          freshness_score: data.data_quality_score * 100,
          coverage_score: Object.keys(data.by_provider).length * 20 // 5 providers max
        };

        if (data.data_quality_score < 0.8) {
          healthMetrics.issues.push('Low data quality score');
        }
        if (data.total_offerings < 100) {
          healthMetrics.issues.push('Limited catalog coverage');
        }

        setHealth(healthMetrics);
      }

    } catch (err: unknown) {
      console.error('Error fetching catalog data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch catalog data');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstances = async () => {
    try {
      setInstancesLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await axios.get(`${getApiUrl()}/api/gpu/discover`, {
        params: { bypass_cache: true, include_stale: true, admin_view: true, limit: 0 },
        headers: { 'Authorization': `Bearer ${token}` },
      });

      setInstances(response.data);
    } catch (err: unknown) {
      console.error('Error fetching instance catalog:', err);
    } finally {
      setInstancesLoading(false);
    }
  };

  const handleRefreshCatalog = async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      await axios.post(`${getApiUrl()}/api/gpu/catalog/refresh`, {
        providers: ['aws', 'gcp'], // Only refresh providers with credentials
        force: true
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Refresh data after triggering update
      setTimeout(() => {
        fetchCatalogData();
        if (instancesExpanded) fetchInstances();
        setRefreshing(false);
      }, 2000);

    } catch (err: unknown) {
      console.error('Error refreshing catalog:', err);
      setRefreshing(false);
    }
  };

  const handleToggleInstances = () => {
    const next = !instancesExpanded;
    setInstancesExpanded(next);
    if (next && instances.length === 0) {
      fetchInstances();
    }
  };

  useEffect(() => {
    fetchCatalogData();
  }, []);

  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-700 rounded"></div>
            <div className="h-3 bg-gray-700 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-red-400 mb-2">GPU Catalog Error</h3>
        <p className="text-red-300">{error}</p>
      </div>
    );
  }

  if (!summary || !health) {
    return (
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-400">GPU Catalog Overview</h3>
        <p className="text-gray-500 mt-2">No catalog data available</p>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-400 bg-green-400/20';
      case 'degraded': return 'text-yellow-400 bg-yellow-400/20';
      case 'critical': return 'text-red-400 bg-red-400/20';
      default: return 'text-gray-400 bg-gray-400/20';
    }
  };

  // Compute instance status counts
  const statusCounts = instances.reduce<Record<InstanceStatus, number>>(
    (acc, gpu) => {
      const s = getInstanceStatus(gpu);
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    { launchable: 0, no_quota: 0, no_image: 0, no_pricing: 0, arch_mismatch: 0 }
  );

  const filteredInstances = statusFilter
    ? instances.filter(gpu => getInstanceStatus(gpu) === statusFilter)
    : instances;

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">GPU Semantic Catalog</h3>
        <button
          onClick={handleRefreshCatalog}
          disabled={refreshing}
          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
        >
          {refreshing ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Refreshing...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </>
          )}
        </button>
      </div>

      {/* Health Status */}
      <div className="flex items-center gap-4 mb-6">
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(health.status)}`}>
          {health.status.charAt(0).toUpperCase() + health.status.slice(1)}
        </div>
        <div className="text-sm text-gray-400">
          Last updated: {new Date(summary.last_updated).toLocaleString()}
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-700/50 rounded-lg p-3">
          <div className="text-2xl font-bold text-white">{summary.total_offerings}</div>
          <div className="text-sm text-gray-400">Total Offerings</div>
        </div>
        <div className="bg-gray-700/50 rounded-lg p-3">
          <div className="text-2xl font-bold text-white">{Object.keys(summary.by_provider).length}</div>
          <div className="text-sm text-gray-400">Providers</div>
        </div>
        <div className="bg-gray-700/50 rounded-lg p-3">
          <div className="text-2xl font-bold text-blue-400">{Math.round(health.freshness_score)}%</div>
          <div className="text-sm text-gray-400">Data Quality</div>
        </div>
        <div className="bg-gray-700/50 rounded-lg p-3">
          <div className="text-2xl font-bold text-green-400">${summary.price_range.min.toFixed(2)}</div>
          <div className="text-sm text-gray-400">Min Price/hr</div>
        </div>
      </div>

      {/* Provider Breakdown */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-300 mb-3">Provider Coverage</h4>
        <div className="space-y-2">
          {Object.entries(summary.by_provider).map(([provider, count]) => {
            const percentage = (count / summary.total_offerings) * 100;
            return (
              <div key={provider} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-blue-600"></div>
                  <span className="text-sm text-gray-300">{provider.toUpperCase()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-600 transition-all"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-400 w-12">{count}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Performance Categories */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-300 mb-3">Performance Categories</h4>
        <div className="flex flex-wrap gap-2">
          {Object.entries(summary.by_category).map(([category, count]) => (
            <span
              key={category}
              className="px-2 py-1 bg-purple-600/20 text-purple-400 rounded-md text-xs"
            >
              {category.replace(/_/g, ' ')}: {count}
            </span>
          ))}
        </div>
      </div>

      {/* Issues */}
      {health.issues.length > 0 && (
        <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-3 mb-6">
          <h4 className="text-sm font-medium text-yellow-400 mb-2">Issues Detected</h4>
          <ul className="text-sm text-yellow-300 space-y-1">
            {health.issues.map((issue, index) => (
              <li key={index} className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {issue}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Instance-Level Catalog Table (collapsible) */}
      <div className="border-t border-gray-700 pt-4">
        <button
          onClick={handleToggleInstances}
          className="flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
        >
          <svg
            className={`w-4 h-4 transition-transform ${instancesExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Instance Catalog
          {instances.length > 0 && (
            <span className="text-gray-500">({instances.length} entries)</span>
          )}
        </button>

        {instancesExpanded && (
          <div className="mt-4">
            {instancesLoading ? (
              <div className="flex items-center gap-2 py-4 text-gray-400 text-sm">
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                Loading catalog...
              </div>
            ) : instances.length === 0 ? (
              <div className="text-sm text-gray-500 py-4">No catalog entries found.</div>
            ) : (
              <>
                {/* Status filter chips */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    onClick={() => setStatusFilter('')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      statusFilter === '' ? 'bg-gray-600 text-white' : 'bg-gray-700/50 text-gray-400 hover:text-white'
                    }`}
                  >
                    All ({instances.length})
                  </button>
                  {(Object.keys(STATUS_CONFIG) as InstanceStatus[]).map(status => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status === statusFilter ? '' : status)}
                      className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors ${
                        statusFilter === status ? 'bg-gray-600 text-white' : 'bg-gray-700/50 text-gray-400 hover:text-white'
                      }`}
                    >
                      <div className={`w-1.5 h-1.5 rounded-full ${STATUS_CONFIG[status].dotClass}`} />
                      {STATUS_CONFIG[status].label} ({statusCounts[status]})
                    </button>
                  ))}
                </div>

                {/* Table */}
                <div className="overflow-x-auto rounded-lg border border-gray-700">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800">
                      <tr className="text-left">
                        <th className="px-3 py-2 text-xs font-medium text-gray-400 uppercase">Provider</th>
                        <th className="px-3 py-2 text-xs font-medium text-gray-400 uppercase">Instance</th>
                        <th className="px-3 py-2 text-xs font-medium text-gray-400 uppercase">GPU</th>
                        <th className="px-3 py-2 text-xs font-medium text-gray-400 uppercase">Region</th>
                        <th className="px-3 py-2 text-xs font-medium text-gray-400 uppercase">Price/hr</th>
                        <th className="px-3 py-2 text-xs font-medium text-gray-400 uppercase">Image</th>
                        <th className="px-3 py-2 text-xs font-medium text-gray-400 uppercase">Quota</th>
                        <th className="px-3 py-2 text-xs font-medium text-gray-400 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/50">
                      {filteredInstances.map(gpu => {
                        const status = getInstanceStatus(gpu);
                        const cfg = STATUS_CONFIG[status];
                        return (
                          <tr key={gpu.id} className="hover:bg-gray-800/30">
                            <td className="px-3 py-2 text-gray-300">{gpu.provider.toUpperCase()}</td>
                            <td className="px-3 py-2 text-white font-mono text-xs">{gpu.provider_instance_id}</td>
                            <td className="px-3 py-2 text-gray-300">
                              {gpu.gpu_count}x {gpu.gpu_model_name || '?'}
                            </td>
                            <td className="px-3 py-2 text-gray-400">{gpu.region}</td>
                            <td className="px-3 py-2 text-gray-300">
                              {gpu.on_demand_price_per_hour != null
                                ? `$${gpu.on_demand_price_per_hour.toFixed(2)}`
                                : <span className="text-gray-500">--</span>
                              }
                            </td>
                            <td className="px-3 py-2 text-gray-300">
                              {gpu.image_status === 'custom_image'
                                ? <span className="text-green-400">Custom ({gpu.image_count})</span>
                                : gpu.image_status === 'base_provider_image'
                                ? <span className="text-blue-400">Base</span>
                                : <span className="text-gray-500">None</span>
                              }
                            </td>
                            <td className="px-3 py-2 text-gray-300">
                              {gpu.quota_remaining != null
                                ? <span>{gpu.quota_remaining}{gpu.quota_unit === 'gpus' ? ' GPUs' : ''} remaining{gpu.quota_group_label ? ` (${gpu.quota_group_label})` : ''}</span>
                                : <span className="text-gray-500">--</span>
                              }
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1.5">
                                <div className={`w-2 h-2 rounded-full ${cfg.dotClass}`} />
                                <span className="text-gray-300">{cfg.label}</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
